import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as api from './RoleCapabilityService';
import RbacPageShell from './RbacPageShell';

// Same rule as the server (RoleCapabilityService, decision D-31): this grant cannot be removed, or nobody could open the Role screens.
const PROTECTED_GRANT = { roleCode: 'UNIVARIETY_SUPER_ADMIN', capabilityKey: 'role.capability.manage' };

const RoleMappingPage = () => {
    const params = useParams();
    const roleId = Number(params.roleId);
    const [role, setRole] = useState<api.RoleRow | null>(null);
    const [capabilities, setCapabilities] = useState<api.CapabilityRow[]>([]);
    const [granted, setGranted] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [actionError, setActionError] = useState('');
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [filter, setFilter] = useState('');
    const [onlyGranted, setOnlyGranted] = useState(false);

    const load = useCallback(async () => {
        if (!roleId || Number.isNaN(roleId)) {
            setError('Invalid role id');
            setLoading(false);
            return;
        }
        setLoading(true);
        setError('');
        try {
            const res = await api.getRoleMapping(roleId);
            setRole(res.data.role);
            setCapabilities(res.data.capabilities || []);
            setGranted(new Set(res.data.grantedCapabilityKeys || []));
        } catch (err) {
            setError(api.errorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [roleId]);

    useEffect(() => {
        load();
    }, [load]);

    const modules = useMemo(() => {
        const q = filter.trim().toLowerCase();
        const map = new Map<string, api.CapabilityRow[]>();
        for (const cap of capabilities) {
            const isGranted = granted.has(cap.capabilityKey);
            if (onlyGranted && !isGranted) continue;
            if (
                q &&
                !cap.capabilityKey.toLowerCase().includes(q) &&
                !(cap.description || '').toLowerCase().includes(q) &&
                !cap.module.toLowerCase().includes(q)
            ) {
                continue;
            }
            const list = map.get(cap.module) || [];
            list.push(cap);
            map.set(cap.module, list);
        }
        return Array.from(map.entries());
    }, [capabilities, filter, granted, onlyGranted]);

    const grantedCount = granted.size;

    const onToggle = async (capabilityKey: string, next: boolean) => {
        if (!role || busyKey) return;
        const previous = granted.has(capabilityKey);
        setActionError('');
        setBusyKey(capabilityKey);
        setGranted((prev) => {
            const copy = new Set(prev);
            if (next) copy.add(capabilityKey);
            else copy.delete(capabilityKey);
            return copy;
        });
        try {
            await api.setGrant({ roleId: role.id, capabilityKey, granted: next });
        } catch (err) {
            setGranted((prev) => {
                const copy = new Set(prev);
                if (previous) copy.add(capabilityKey);
                else copy.delete(capabilityKey);
                return copy;
            });
            setActionError(api.errorMessage(err));
        } finally {
            setBusyKey(null);
        }
    };

    if (loading) {
        return (
            <RbacPageShell title="Role capability mapping">
                <p className="mb-0">Loading…</p>
            </RbacPageShell>
        );
    }

    if (error || !role) {
        return (
            <RbacPageShell title="Role capability mapping">
                <div className="alert alert-danger" role="alert">
                    {error || 'Role not found'}
                </div>
            </RbacPageShell>
        );
    }

    return (
        <RbacPageShell
            title={role.name}
            subtitle={`${role.roleCode} · ${role.type}`}
            links={[{ to: '/support/role-capabilities', label: '← All roles' }]}
        >
            {actionError ? (
                <div className="alert alert-danger" role="alert">
                    {actionError}
                </div>
            ) : null}

            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 filterSearch gap-2">
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <span className="font-14">
                        {grantedCount} granted · {capabilities.length} total
                    </span>
                    <label className="font-14 mb-0 d-flex align-items-center gap-2">
                        <input
                            type="checkbox"
                            className="form-check-input m-0"
                            checked={onlyGranted}
                            onChange={(e) => setOnlyGranted(e.target.checked)}
                        />
                        Show granted only
                    </label>
                </div>
                <div style={{ minWidth: 240, maxWidth: 360 }} className="flex-grow-1 flex-md-grow-0">
                    <input
                        className="form-control"
                        placeholder="Filter capabilities…"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />
                </div>
            </div>

            <div className="table-responsive attendee_dtl" style={{ maxHeight: '65vh' }}>
                <table className="table table-hover">
                    <thead>
                        <tr>
                            <th style={{ width: 70 }}>Grant</th>
                            <th>Capability</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        {modules.map(([moduleName, caps]) => (
                            <Fragment key={moduleName}>
                                <tr className="table-light">
                                    <td colSpan={3} className="font-600">
                                        {moduleName}
                                    </td>
                                </tr>
                                {caps.map((cap) => {
                                    const checked = granted.has(cap.capabilityKey);
                                    const locked =
                                        role.roleCode === PROTECTED_GRANT.roleCode && cap.capabilityKey === PROTECTED_GRANT.capabilityKey;
                                    return (
                                        <tr key={cap.capabilityKey}>
                                            <td className="text-center align-middle">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={checked}
                                                    disabled={locked || busyKey === cap.capabilityKey}
                                                    title={locked ? 'Required: without it nobody could open the Role screens' : undefined}
                                                    aria-label={cap.capabilityKey}
                                                    onChange={(e) => onToggle(cap.capabilityKey, e.target.checked)}
                                                />
                                            </td>
                                            <td>{cap.capabilityKey}</td>
                                            <td className="text-muted">{cap.description || '—'}</td>
                                        </tr>
                                    );
                                })}
                            </Fragment>
                        ))}
                        {!modules.length ? (
                            <tr>
                                <td colSpan={3} className="text-muted">
                                    No capabilities match the filters.
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </div>
        </RbacPageShell>
    );
};

export default RoleMappingPage;
